import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { IconType } from 'react-icons'
import {
  FiFileText, FiTarget, FiUsers, FiUser, FiZap,
  FiLogOut, FiDroplet, FiCode,
} from 'react-icons/fi'
import { useNavigationItems } from '../layout/navigation/useNavigationItems'
import { getAdminCategories } from '../layout/navigation/adminNavData'
import { useAuthStore } from '../../store'
import { selectIsPlatformAdmin } from '../../store/useAuthStore'
import { mockDb } from '../../api/mockDb'
import type { Challenge } from '../../types/challenge.types'
import type { Community } from '../../types/community.types'
import type { UserRole } from '../../types'

interface PaletteUser {
  id: string
  displayName: string
  email: string
  role: UserRole
}

export interface PaletteItem {
  id: string
  group: 'Pages' | 'Challenges' | 'Communities' | 'Actions' | 'Users'
  label: string
  description?: string
  icon: IconType
  action: { type: 'navigate'; path: string } | { type: 'callback'; fn: () => void }
}

export function useCommandPaletteItems() {
  const { t } = useTranslation()
  const { sections } = useNavigationItems()
  const isPlatformAdmin = useAuthStore(selectIsPlatformAdmin)

  return useMemo(() => {
    const items: PaletteItem[] = []
    const seen = new Set<string>()

    // 1. Pages — flatten navigation sections
    for (const section of sections) {
      for (const item of section.items) {
        if (item.external) continue
        const id = `page-${item.to}`
        if (seen.has(id)) continue
        seen.add(id)
        items.push({
          id,
          group: 'Pages',
          label: item.label,
          description: section.title,
          icon: item.icon ?? FiFileText,
          action: { type: 'navigate', path: item.to },
        })
      }
      // Also include sub-section items (e.g. community nav items)
      if (section.subSections) {
        for (const sub of section.subSections) {
          for (const item of sub.items) {
            const id = `page-${item.to}`
            if (seen.has(id)) continue
            seen.add(id)
            items.push({
              id,
              group: 'Pages',
              label: item.label,
              description: `${section.title} · ${sub.title}`,
              icon: item.icon ?? FiFileText,
              action: { type: 'navigate', path: item.to },
            })
          }
        }
      }
    }

    // Include all admin tool pages (sidebar now only shows category headers)
    if (isPlatformAdmin) {
      const adminCategories = getAdminCategories(t)
      for (const cat of adminCategories) {
        for (const item of cat.items) {
          if (item.external) continue
          const id = `page-${item.to}`
          if (seen.has(id)) continue
          seen.add(id)
          items.push({
            id,
            group: 'Pages',
            label: item.label,
            description: `Admin · ${cat.title}`,
            icon: item.icon ?? FiFileText,
            action: { type: 'navigate', path: item.to },
          })
        }
      }
    }

    // 2. Challenges — from mockDb
    const challenges = mockDb.getAll<Challenge>('challenges')
    for (const ch of challenges.slice(0, 50)) {
      items.push({
        id: `challenge-${ch.id}`,
        group: 'Challenges',
        label: ch.title,
        description: ch.difficulty ? `${ch.difficulty} difficulty` : undefined,
        icon: FiTarget,
        action: { type: 'navigate', path: `/challenges/${ch.id}` },
      })
    }

    // 3. Communities — from mockDb
    const communities = mockDb.getAll<Community>('communities')
    for (const c of communities.slice(0, 30)) {
      items.push({
        id: `community-${c.id}`,
        group: 'Communities',
        label: c.name,
        description: c.type,
        icon: FiUsers,
        action: { type: 'navigate', path: `/communities/${c.id}` },
      })
    }

    // 4. Quick Actions
    items.push({
      id: 'action-profile',
      group: 'Actions',
      label: 'View Profile',
      icon: FiUser,
      action: { type: 'navigate', path: '/account' },
    })
    items.push({
      id: 'action-logout',
      group: 'Actions',
      label: 'Log Out',
      icon: FiLogOut,
      action: { type: 'callback', fn: () => useAuthStore.getState().logout() },
    })
    if (import.meta.env.DEV) {
      items.push({
        id: 'action-debug',
        group: 'Actions',
        label: 'Toggle Debug Panel',
        description: 'Ctrl + `',
        icon: FiCode,
        action: {
          type: 'callback',
          fn: () => window.dispatchEvent(new KeyboardEvent('keydown', { key: '`', ctrlKey: true, bubbles: true })),
        },
      })
    }
    if (isPlatformAdmin) {
      items.push({
        id: 'action-theme',
        group: 'Actions',
        label: 'Theme Editor',
        icon: FiDroplet,
        action: { type: 'navigate', path: '/admin/theme-editor' },
      })
    }

    // 5. Users (admin only)
    if (isPlatformAdmin) {
      const users = mockDb.getAll<PaletteUser>('users')
      for (const u of users.slice(0, 30)) {
        items.push({
          id: `user-${u.id}`,
          group: 'Users',
          label: u.displayName,
          description: `${u.email} · ${u.role}`,
          icon: FiUser,
          action: { type: 'navigate', path: `/admin/users/${u.id}` },
        })
      }
    }

    return items
  }, [sections, isPlatformAdmin, t])
}
